import { useState, useEffect } from 'react';
import { Shield, Loader2, AlertCircle, CheckCircle2, XCircle, Building2, Clock, Mail } from 'lucide-react';
import { authenticatedFetch } from '@/lib/auth';
import { notifyApprovalsChanged } from '@/lib/usePendingApprovalCounts';

interface AdminRequest {
  id: string;
  target_email: string;
  target_user_id: string | null;
  invitation_id: string | null;
  requested_at: string;
  notes: string | null;
  member_id: string;
  organization_name: string;
  study_association_name: string;
  requested_by_email: string | null;
  requested_by_name: string | null;
  invitation_accepted_at: string | null;
  target_currently_admin: boolean | null;
}

export default function PendingMemberAdminRequests() {
  const [pending, setPending] = useState<AdminRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [actingId, setActingId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => { load(); }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await authenticatedFetch('/api/admin/member-admin-requests/pending');
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Kunne ikke laste');
      setPending(d.pending || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function approve(id: string) {
    setActingId(id);
    setError(null);
    try {
      const r = await authenticatedFetch(`/api/admin/member-admin-requests/${id}/approve`, { method: 'PATCH' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setSuccess('Forespørsel godkjent. Foreslår er varslet.');
      notifyApprovalsChanged();
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActingId(null);
    }
  }

  async function reject(id: string) {
    setActingId(id);
    setError(null);
    try {
      const r = await authenticatedFetch(`/api/admin/member-admin-requests/${id}/reject`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: rejectReason.trim() || null })
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error);
      setSuccess('Forespørsel avvist. Foreslår er varslet.');
      setRejectingId(null);
      setRejectReason('');
      notifyApprovalsChanged();
      await load();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setActingId(null);
    }
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Shield size={22} />
          Forespørsler om medlem-admin-rolle
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Når en medlem-administrator inviterer eller utpeker en kollega som administrator, må du
          godkjenne forespørselen før admin-rollen tildeles.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-800 m-0">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4 flex items-start gap-2">
          <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-green-800 m-0">{success}</p>
        </div>
      )}

      {loading ? (
        <div className="text-center py-8 text-gray-500">
          <Loader2 className="w-6 h-6 animate-spin mx-auto" />
        </div>
      ) : pending.length === 0 ? (
        <div className="text-center py-8 text-gray-500 border border-dashed rounded-lg">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-green-300" />
          <p>Ingen ventende forespørsler.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {pending.map((p) => (
            <div key={p.id} className="border border-purple-200 bg-purple-50 rounded-lg p-4">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    <Mail size={16} className="text-purple-700" />
                    <strong className="text-gray-900">{p.target_email}</strong>
                    {p.invitation_id && !p.invitation_accepted_at && (
                      <span className="px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full flex items-center gap-1">
                        <Clock size={10} /> Invitasjon ikke akseptert ennå
                      </span>
                    )}
                    {p.target_user_id && (
                      <span className="px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
                        Aktiv bruker
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-700 flex items-center gap-2 mb-1">
                    <Building2 size={14} />
                    <span>{p.organization_name}</span>
                    <span className="text-gray-400">·</span>
                    <span className="text-gray-500">{p.study_association_name}</span>
                  </div>
                  <div className="text-xs text-gray-600">
                    Foreslått {new Date(p.requested_at).toLocaleDateString('nb-NO')}
                    {p.requested_by_name ? ` av ${p.requested_by_name}` : ''}
                    {p.requested_by_email ? ` (${p.requested_by_email})` : ''}
                  </div>
                  {p.notes && <p className="text-sm text-gray-700 mt-2 italic bg-white/60 rounded p-2">{p.notes}</p>}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => approve(p.id)}
                    disabled={actingId === p.id}
                    className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 text-sm"
                  >
                    {actingId === p.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                    Godkjenn
                  </button>
                  <button
                    onClick={() => { setRejectingId(p.id); setRejectReason(''); }}
                    disabled={actingId === p.id}
                    className="px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center gap-2 text-sm"
                  >
                    <XCircle size={14} />
                    Avvis
                  </button>
                </div>
              </div>

              {rejectingId === p.id && (
                <div className="mt-3 pt-3 border-t border-purple-300">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Begrunnelse (sendes til foreslår)</label>
                  <textarea
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    rows={2}
                    placeholder="F.eks. 'Vi vil ha en samtale først'"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                  <div className="flex justify-end gap-2 mt-2">
                    <button onClick={() => { setRejectingId(null); setRejectReason(''); }} className="px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded">Avbryt</button>
                    <button onClick={() => reject(p.id)} disabled={actingId === p.id} className="px-3 py-1.5 text-sm bg-red-600 text-white rounded disabled:opacity-50">
                      Bekreft avvisning
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
